import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Chip, Tooltip, IconButton, Divider, useTheme,
} from '@mui/material';
import DashboardIcon    from '@mui/icons-material/Dashboard';
import ApartmentIcon    from '@mui/icons-material/Apartment';
import PeopleIcon       from '@mui/icons-material/People';
import LogoutIcon       from '@mui/icons-material/Logout';
import HistoryIcon      from '@mui/icons-material/History';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import SyncIcon         from '@mui/icons-material/Sync';
import CloudUploadIcon  from '@mui/icons-material/CloudUpload';
import SettingsIcon     from '@mui/icons-material/Settings';
import BarChartIcon     from '@mui/icons-material/BarChart';
import LightModeIcon    from '@mui/icons-material/LightMode';
import DarkModeIcon     from '@mui/icons-material/DarkMode';
import { useAuth }      from '../utils/AuthContext';
import { useThemeMode } from '../utils/ThemeContext';

const DRAWER_W = 240;

const NAV = [
  { label: 'Dashboard', path: '/',         icon: <DashboardIcon />,    role: 'readonly' },
  { label: 'Tenants',   path: '/tenants',  icon: <ApartmentIcon />,    role: 'readonly' },
  { label: 'Sync',      path: '/sync',     icon: <SyncIcon />,         role: 'operator' },
  { label: 'Provision', path: '/provision', icon: <CloudUploadIcon />, role: 'admin' },
  { label: 'Scan',      path: '/scan',     icon: <TroubleshootIcon />, role: 'readonly' },
  { label: 'Users',     path: '/users',    icon: <PeopleIcon />,       role: 'admin' },
  { label: 'Analytics', path: '/analytics', icon: <BarChartIcon />,    role: 'readonly' },
  { label: 'Audit Log', path: '/audit',    icon: <HistoryIcon />,      role: 'readonly' },
  { label: 'Settings',  path: '/settings', icon: <SettingsIcon />,     role: 'admin' },
];

const ROLE_COLORS = {
  super_admin: '#7c3aed',
  admin:       '#3b82f6',
  operator:    '#16a34a',
  readonly:    '#6b778c',
};

export default function Layout({ children }) {
  const { user, logout, hasRole } = useAuth();
  const { mode, toggleMode }      = useThemeMode();
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const dark      = mode === 'dark';

  const sidebarBg     = dark ? '#0d1117' : '#0f1f35';
  const activeBg      = dark ? 'rgba(59,130,246,0.18)' : 'rgba(59,130,246,0.22)';
  const hoverBg       = dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.07)';
  const borderColor   = 'rgba(255,255,255,0.07)';
  const textMuted     = 'rgba(255,255,255,0.5)';
  const textActive    = '#fff';
  const iconActive    = '#93c5fd';
  const iconInactive  = 'rgba(255,255,255,0.45)';

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: sidebarBg }}>
      {/* Logo */}
      <Box sx={{ px: 2.5, pt: 3, pb: 2.5, borderBottom: `1px solid ${borderColor}` }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', letterSpacing: 0.3, lineHeight: 1.2 }}>
          HOA Control
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.6rem' }}>
          Admin Panel
        </Typography>
      </Box>

      {/* Nav */}
      <List sx={{ px: 1.5, py: 1.5, flex: 1 }}>
        {NAV.filter(n => hasRole(n.role)).map(item => {
          const active = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <ListItemButton key={item.path} component={Link} to={item.path}
              sx={{
                borderRadius: '10px', mb: 0.5, py: 0.9, px: 1.25,
                backgroundColor: active ? activeBg : 'transparent',
                borderLeft: active ? '2px solid #3b82f6' : '2px solid transparent',
                transition: 'all 0.15s ease',
                '&:hover': { backgroundColor: active ? activeBg : hoverBg },
              }}
            >
              <ListItemIcon sx={{ minWidth: 34, color: active ? iconActive : iconInactive }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.85rem',
                  fontWeight: active ? 600 : 400,
                  color: active ? textActive : textMuted,
                  letterSpacing: 0.1,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* User footer */}
      <Box sx={{ px: 1.5, py: 2, borderTop: `1px solid ${borderColor}` }}>
        {/* Theme toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Tooltip title={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton
              size="small"
              onClick={toggleMode}
              sx={{
                color: 'rgba(255,255,255,0.45)',
                backgroundColor: 'rgba(255,255,255,0.06)',
                borderRadius: '8px',
                px: 1,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
              }}
            >
              {dark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar sx={{
            width: 32, height: 32, fontSize: '0.8rem', flexShrink: 0,
            backgroundColor: ROLE_COLORS[user?.role] || '#3b82f6',
            fontWeight: 700,
          }}>
            {user?.username?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
              {user?.username}
            </Typography>
            <Chip
              label={user?.role?.replace('_', ' ')}
              size="small"
              sx={{
                height: 15, fontSize: '0.6rem', fontWeight: 700,
                backgroundColor: ROLE_COLORS[user?.role],
                color: '#fff', mt: 0.25,
              }}
            />
          </Box>
          <Tooltip title="Sign out">
            <IconButton
              size="small"
              onClick={() => { logout(); navigate('/login'); }}
              sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#f87171' }, flexShrink: 0 }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_W,
          '& .MuiDrawer-paper': {
            width: DRAWER_W,
            border: 'none',
            boxShadow: dark ? '4px 0 24px rgba(0,0,0,0.5)' : '4px 0 24px rgba(0,0,0,0.1)',
          },
        }}
      >
        {sidebar}
      </Drawer>

      <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto', minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
}
